<template>
  <div>
    <v-container class="pa-0 scrollable" fluid>
      <v-card v-if="feature" color="transparent" class="hyp-card" flat>
        <v-row>
          <v-col>
            <h2>{{ locationName }}</h2>
          </v-col>
        </v-row>

        <v-row v-if="location">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Location</div>
            <div class="hyp-text mt-1">
              {{ location }}
            </div>
          </v-col>
        </v-row>

        <v-row v-if="createdDate">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Incident Creation</div>
            <div class="hyp-text mt-1">
              <div class="pull-left">
                <v-icon color="black" size="15" class="mr-1"
                  >far fa-calendar-alt</v-icon
                >
                {{ createdDate }}
              </div>
              <div class="pull-left ml-3">
                <v-icon color="black" size="15" class="mr-1"
                  >far fa-clock</v-icon
                >
                {{ createdTime }} UTC
              </div>
              <div class="clearfix"></div>
            </div>
          </v-col>
        </v-row>

        <hr class="hyp-break mt-2" />

        <v-row>
          <v-col v-if="severityScore" class="hyp-text text-center" cols="3">
            <div class="hyp-severity-score application-heading">
              {{ severityScore }}
            </div>
            <div class="hyp-highlight-text application-heading">Severity</div>
          </v-col>
          <v-col class="hyp-text text-center" cols="3">
            <div>
              <img
                class="categoryImage"
                :src="mainCategoryImageSrc"
                alt="category type"
              />
            </div>
            <div class="hyp-highlight-text application-heading">
              {{ mainCategoryName }}
            </div>
          </v-col>
          <v-col class="hyp-text" cols="6">
            <div class="hyp-heading application-heading">
              Precision Location / Region
            </div>
            <div class="hyp-text">
              <ul>
                <li>{{ locationPrecisionName }}</li>
                <li>{{ regionName }}</li>
              </ul>
            </div>
          </v-col>
        </v-row>

        <hr v-if="description" class="hyp-break mb-3 mt-2" />

        <v-row v-if="description">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Description</div>
            <div class="hyp-text mt-1">
              {{ description }}
            </div>
          </v-col>
        </v-row>

        <hr
          v-if="perpatrators || injuries || fatalities"
          class="hyp-break mb-3 mt-2"
        />

        <v-row v-if="perpatrators">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Perpetrators</div>
            <div class="hyp-text mt-1">
              <ul>
                <li v-for="perpatrator in perpatrators" :key="perpatrator.Id">
                  {{ perpatrator.Name }}
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="injuries">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Injuries</div>
            <div class="hyp-text mt-1">
              {{ injuries }}
            </div>
          </v-col>
        </v-row>

        <v-row v-if="fatalities">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Fatalities</div>
            <div class="hyp-text mt-1">
              {{ fatalities }}
            </div>
          </v-col>
        </v-row>

        <hr v-if="sectors" class="hyp-break mb-3 mt-2" />

        <v-row v-if="sectors">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Affected Sectors</div>
            <v-row dense class="mt-1">
              <v-col cols="3" v-for="sector in sectors" :key="sector.Id">
                <incidents-badge
                  :type="'sector'"
                  :title="sector.Name"
                  :id="sector.Id"
                ></incidents-badge>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <hr v-if="assetTypes" class="hyp-break mb-3 mt-2" />

        <v-row v-if="assetTypes">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Asset Types</div>
            <div class="hyp-text mt-1">
              <ul>
                <li v-for="assetType in assetTypes" :key="assetType.Id">
                  {{ assetType.Name }}
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>

        <hr v-if="categories" class="hyp-break mb-3 mt-2" />

        <v-row v-if="categories">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">Categories</div>
            <v-row dense class="mt-1">
              <v-col cols="3" v-for="category in categories" :key="category.Id">
                <incidents-badge
                  :type="'category'"
                  :title="category.Name"
                  :id="category.Id"
                ></incidents-badge>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <hr v-if="modifiedDate || modifiedTime" class="hyp-break mb-3 mt-2" />

        <v-row v-if="modifiedDate || modifiedTime">
          <v-col class="hyp-text pt-0">
            <div class="hyp-heading application-heading">
              Incident Modification
            </div>
            <div class="hyp-text mt-1">
              <div v-if="modifiedDate" class="pull-left">
                <v-icon color="black" size="15" class="mr-1"
                  >far fa-calendar-alt</v-icon
                >
                {{ modifiedDate }}
              </div>
              <div v-if="modifiedTime" class="pull-left ml-3">
                <v-icon color="black" size="15" class="mr-1"
                  >far fa-clock</v-icon
                >
                {{ modifiedTime }} UTC
              </div>
              <div class="clearfix"></div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <div v-if="!feature">
        <Loader size="small" color="black" text="" class="ml-2 pt-10"></Loader>
      </div>
    </v-container>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashGet from "lodash/get";
import lodashHas from "lodash/has";

import { getCategoryIcon } from "@/services/controlRisks/incidents.service";

export default {
  name: "IncidentsDetailPolitical",
  components: {
    IncidentsBadge: () =>
      import("@/components/controlRisks/incidents/IncidentsBadge"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    feature: {
      type: Object,
      required: true,
    },
  },
  computed: {
    mainCategoryName() {
      if (lodashGet(this.feature, "Categories[0].Name")) {
        return lodashGet(this.feature, "Categories[0].Name");
      }
      if (lodashGet(this.feature, "Category.Name")) {
        return lodashGet(this.feature, "Category.Name");
      }
      return null;
    },
    mainCategoryImageSrc() {
      if (lodashGet(this.feature, "Categories[0].Id")) {
        return getCategoryIcon(lodashGet(this.feature, "Categories[0].Id"));
      }
      if (lodashGet(this.feature, "Category.Id")) {
        return getCategoryIcon(lodashGet(this.feature, "Category.Id"));
      }
      return null;
    },
    categories() {
      if (lodashGet(this.feature, "Categories")) {
        return lodashGet(this.feature, "Categories");
      }
      if (lodashGet(this.feature, "Category")) {
        return [lodashGet(this.feature, "Category")];
      }
      return null;
    },
    locationName() {
      if (lodashHas(this.feature, "Country.Name")) {
        return lodashGet(this.feature, "Country.Name");
      }
      return null;
    },
    location() {
      if (
        lodashGet(this.feature, "Location.Lat") &&
        lodashGet(this.feature, "Location.Lon")
      ) {
        return `${this.feature.Location.Lat.toFixed(
          5
        )}, ${this.feature.Location.Lon.toFixed(5)}`;
      }
      return null;
    },
    createdDate() {
      if (this.feature.Created) {
        return dayjs(this.feature.Created).format("MMM D, YYYY");
      }
      return null;
    },
    createdTime() {
      if (this.feature.Created) {
        return dayjs(this.feature.Created).format("hh:mm");
      }
      return null;
    },
    modifiedDate() {
      if (this.feature.Modified) {
        return dayjs(this.feature.Modified).format("MMM D, YYYY");
      }
      return null;
    },
    modifiedTime() {
      if (this.feature.Modified) {
        return dayjs(this.feature.Modified).format("hh:mm");
      }
      return null;
    },
    modifiedDateDiffers() {
      return this.createdDate !== this.modifiedDate;
    },
    modifiedTimeDiffers() {
      return this.createdTime !== this.modifiedTime;
    },
    injuries() {
      if (this.feature && this.feature.TotalInjuries) {
        return this.feature.TotalInjuries;
      }
      return null;
    },
    fatalities() {
      if (this.feature && this.feature.TotalFatalities) {
        return this.feature.TotalFatalities;
      }
      return null;
    },
    perpatrators() {
      if (this.feature && this.feature.Perpetrators) {
        return this.feature.Perpetrators;
      }
      return null;
    },
    sectors() {
      if (this.feature && this.feature.Sectors) {
        return this.feature.Sectors;
      }
      return null;
    },
    assetTypes() {
      if (this.feature && this.feature.AssetTypes) {
        return this.feature.AssetTypes;
      }
      return null;
    },
    severityScore() {
      if (this.feature && this.feature.SeverityScore) {
        return this.feature.SeverityScore;
      }
      return null;
    },
    locationPrecisionName() {
      if (lodashHas(this.feature, "LocationPrecision.Name")) {
        return lodashGet(this.feature, "LocationPrecision.Name");
      }
      return null;
    },
    regionName() {
      if (lodashHas(this.feature, "Region.Name")) {
        return lodashGet(this.feature, "Region.Name");
      }
      return null;
    },
    description() {
      if (this.feature && this.feature.Description) {
        return this.feature.Description;
      }
      return null;
    },
  },
};
</script>

<style scoped>
.hyp-heading {
  font-size: 11px;
}
.hyp-text {
  font-size: 12px;
  color: #1c1c1c;
}
.hyp-break {
  height: 1px;
  background-color: #ccc;
  border: none;
}
.hyp-severity-score {
  font-size: 32px;
}
.hyp-highlight-text {
  font-size: 14px;
}
.categoryImage {
  width: 44px;
  height: 44px;
}
.scrollable {
  height: 100vh;
  overflow-y: auto;
}
</style>
